/**
 * Printable V2MOM — same paper / Paged.js treatment as the Strategy Canvas print.
 *
 * Route: /prioritization/strategy/v2mom/:v2momId/print
 */

import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { useV2momStore } from '@/stores/prioritization/v2momStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useOrgUnits, useEnterpriseIdentityStore } from '@/stores/prioritization/enterpriseIdentityStore';
import type { PrintPaperId } from '@/lib/print/paperPresets';
import {
  PRINT_PAPER_PRESETS,
  getPrintPaperPreset,
  loadSavedPrintPaperId,
  savePrintPaperId,
} from '@/lib/print/paperPresets';
import { PrintChrome } from '@/components/print/PrintChrome';
import { V2momPrintBody } from '@/components/print/V2momPrintBody';
import { Select } from '@/components/ds';

export function V2momPrintPage() {
  const { v2momId } = useParams<{ v2momId: string }>();
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);

  const v2moms = useV2momStore((s) => s.v2moms);
  const entriesByDoc = useV2momStore((s) => s.entriesByDoc);
  const isLoading = useV2momStore((s) => s.isLoading);
  const fetchV2moms = useV2momStore((s) => s.fetchV2moms);
  const orgUnits = useOrgUnits();
  const fetchOrgUnits = useEnterpriseIdentityStore((s) => s.fetchFromSupabase);

  const [paperId, setPaperId] = useState<PrintPaperId>(() => loadSavedPrintPaperId());

  useEffect(() => {
    savePrintPaperId(paperId);
  }, [paperId]);

  useEffect(() => {
    if (!workspaceId) return;
    void fetchV2moms();
    void fetchOrgUnits({ workspaceId });
  }, [workspaceId, fetchV2moms, fetchOrgUnits]);

  const v2mom = v2momId ? v2moms.find((d) => d.id === v2momId) : undefined;
  const entries = useMemo(() => (v2momId ? entriesByDoc[v2momId] ?? [] : []), [entriesByDoc, v2momId]);
  const parentTitle = v2mom?.parentId ? (v2moms.find((d) => d.id === v2mom.parentId)?.title ?? null) : null;
  const orgUnit = v2mom?.orgUnitId ? orgUnits.find((u) => u.id === v2mom.orgUnitId) : undefined;
  const orgUnitName = orgUnit ? orgUnit.displayName || orgUnit.name : null;

  const preset = useMemo(() => getPrintPaperPreset(paperId), [paperId]);

  const docTitle = v2mom?.title?.trim()
    ? `${v2mom.title.trim()} — V2MOM print`
    : v2momId
      ? `V2MOM ${v2momId.slice(0, 8)}… — Print`
      : 'Print';

  const pagedRenderKey = useMemo(() => {
    if (!v2mom) return '';
    const latestEntry = entries.reduce((max, e) => (e.updatedAt > max ? e.updatedAt : max), '');
    return [v2mom.id, v2mom.updatedAt, paperId, entries.length, latestEntry, parentTitle ?? '', orgUnitName ?? ''].join('|');
  }, [v2mom, entries, paperId, parentTitle, orgUnitName]);

  const handlePaperChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setPaperId(e.target.value as PrintPaperId);
  }, []);

  if (!v2momId) {
    return <div className="p-8 text-center text-sm text-[var(--text-sec)]">No V2MOM selected.</div>;
  }
  if (!workspaceId) {
    return <div className="p-8 text-center text-sm text-[var(--text-sec)]">Select a workspace to print this V2MOM.</div>;
  }
  if (!v2mom && isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center gap-2">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
        <span className="text-[var(--text-ter)]">Loading V2MOM…</span>
      </div>
    );
  }
  if (!v2mom) {
    return <div className="p-8 text-center text-sm text-[var(--text-sec)]">V2MOM not found.</div>;
  }

  return (
    <PrintChrome
      documentTitle={docTitle}
      pageSizeCss={preset.pageSizeCss}
      previewMaxWidthMm={preset.previewMaxWidthMm}
      backHref={`/prioritization/strategy/v2mom/${v2momId}`}
      backLabel="Return to V2MOM"
      pagedRenderKey={pagedRenderKey}
      toolbarAside={
        <label className="flex flex-wrap items-center gap-2 text-xs text-[var(--text-sec)] print:hidden">
          <span>Paper size</span>
          <Select
            size="sm"
            value={paperId}
            onChange={handlePaperChange}
            options={PRINT_PAPER_PRESETS.map((p) => ({ value: p.id, label: p.label }))}
          />
        </label>
      }
    >
      <V2momPrintBody v2mom={v2mom} entries={entries} parentTitle={parentTitle} orgUnitName={orgUnitName} />
    </PrintChrome>
  );
}

export default V2momPrintPage;
